import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { SecurityPinSetupActions, SecurityPinSetupBody, SecurityPinStepper } from "./SecurityPinSetup";
import { useSecurityPinSetup } from "./useSecurityPinSetup";

export type SecurityPinDialogMode = "create" | "change" | "reset";

export interface SecurityPinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `change` replaces a working PIN; `reset` is the way out of a lock or a forgotten PIN. */
  mode: SecurityPinDialogMode;
  /** Runs after the PIN is saved and the person closes the success state. */
  onDone?: () => void;
}

const TITLE_KEYS = {
  create: "security_pin_dialog_create_title",
  change: "security_pin_dialog_change_title",
  reset: "security_pin_dialog_reset_title",
} as const;

const SUBMIT_KEYS = {
  create: "security_pin_submit_create",
  change: "security_pin_submit_change",
  reset: "security_pin_submit_change",
} as const;

/** The PIN flow in a dialog: the stepper stays in the toolbar, the actions in the footer. */
export function SecurityPinDialog({ open, onOpenChange, mode, onDone }: SecurityPinDialogProps) {
  return open ? <SecurityPinDialogContent onOpenChange={onOpenChange} mode={mode} onDone={onDone} /> : null;
}

/** Mounted only while open, so every opening starts from an empty first step. */
function SecurityPinDialogContent({ onOpenChange, mode, onDone }: Omit<SecurityPinDialogProps, "open">) {
  const { t } = useTranslations("global");
  const controller = useSecurityPinSetup();
  const done = controller.step === "done";

  const finish = () => {
    onOpenChange(false);
    onDone?.();
  };

  return (
    <ColocaDialog
      open
      onOpenChange={(next) => {
        if (!next && controller.saving) return;
        if (!next && done) return finish();
        onOpenChange(next);
      }}
      size="sm"
      title={t(TITLE_KEYS[mode])}
      description={done ? undefined : t("security_pin_dialog_description")}
      toolbar={done ? undefined : <SecurityPinStepper step={controller.step} />}
      footer={
        <SecurityPinSetupActions
          controller={controller}
          submitLabel={t(SUBMIT_KEYS[mode])}
          done={{ label: t("security_pin_done_close"), onClick: finish }}
          leading={
            <ColocaButton variant="ghost" onClick={() => onOpenChange(false)}>
              {t("two_factor_required_modal_cancel")}
            </ColocaButton>
          }
        />
      }
    >
      <SecurityPinSetupBody controller={controller} />
    </ColocaDialog>
  );
}
